import { PureComponent, MouseEvent, ChangeEvent } from 'react';

import { FormattedMessage } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';

import { commonMessages } from 'src/i18n';

import { FileEditorMode } from '../types';

import { FileEditorModeChangeEvent } from './file';
import messages from './file-editor-footer.i18n';

const footerStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  borderTopColor: 'color.border',
  borderTopStyle: 'solid',
  borderTopWidth: 'border.width',
  padding: 'space.100',
});

type Props = {
  canPreview?: boolean;
  fileEditorMode: FileEditorMode;
  hasUnsavedChanges?: boolean;
  onCancel?: (e: MouseEvent<HTMLElement>) => void;
  onChangeFileEditorMode: (e: FileEditorModeChangeEvent) => void;
  onCommit?: () => void;
};

export default class FileEditorFooter extends PureComponent<Props> {
  static defaultProps = {
    canPreview: false,
    /* eslint react/default-props-match-prop-types: "warn" */
    fileEditorMode: 'editor',
    hasUnsavedChanges: false,
    onCancel: () => {},
    onChangeFileEditorMode: () => {},
    onCommit: () => {},
  };

  handleToggleMarkupPreview = (e: ChangeEvent<HTMLInputElement>) => {
    this.props.onChangeFileEditorMode({
      event: e,
      fileEditorMode:
        this.props.fileEditorMode === 'editor-preview'
          ? 'editor'
          : 'editor-preview',
    });
  };

  handleToggleDiff = (e: ChangeEvent<HTMLInputElement>) => {
    this.props.onChangeFileEditorMode({
      event: e,
      fileEditorMode:
        this.props.fileEditorMode === 'editor-diff' ? 'editor' : 'editor-diff',
    });
  };

  render() {
    const { canPreview, fileEditorMode, hasUnsavedChanges, onCommit } =
      this.props;

    return (
      <Inline
        alignBlock="center"
        alignInline="end"
        space="space.250"
        xcss={footerStyles}
      >
        {fileEditorMode !== 'read-only' && (
          <Inline alignBlock="center">
            <Toggle
              id="editor-diff-toggle"
              isChecked={fileEditorMode === 'editor-diff'}
              isDisabled={
                !hasUnsavedChanges ||
                (fileEditorMode !== 'editor' &&
                  fileEditorMode !== 'editor-diff')
              }
              onChange={this.handleToggleDiff}
            />
            <Box>
              <FormattedMessage {...messages.viewEditDiff}>
                {txt => <label htmlFor="editor-diff-toggle">{txt}</label>}
              </FormattedMessage>
            </Box>
          </Inline>
        )}
        {fileEditorMode !== 'read-only' && canPreview && (
          <Inline alignBlock="center">
            <Toggle
              id="editor-markup-preview-toggle"
              isChecked={fileEditorMode === 'editor-preview'}
              isDisabled={
                fileEditorMode !== 'editor' &&
                fileEditorMode !== 'editor-preview'
              }
              onChange={this.handleToggleMarkupPreview}
            />
            <Box>
              <FormattedMessage {...messages.preview}>
                {txt => (
                  <label htmlFor="editor-markup-preview-toggle">{txt}</label>
                )}
              </FormattedMessage>
            </Box>
          </Inline>
        )}
        <ButtonGroup>
          <Button
            appearance="primary"
            isDisabled={!hasUnsavedChanges}
            onClick={onCommit}
          >
            <FormattedMessage {...commonMessages.commit} />
          </Button>
          <Button appearance="subtle" onClick={this.props.onCancel}>
            <FormattedMessage {...commonMessages.cancel} />
          </Button>
        </ButtonGroup>
      </Inline>
    );
  }
}
